import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { tileSourceRect } from '../../../game/tileSheet';

/**
 * Pick a tile out of the actual tileset.
 *
 * REPLACES A HARDCODED LIST OF FOUR. The palette used to name gids 1-4 in
 * source, because the placeholder sheet had five usable tiles and no metadata
 * saying which was which. It now reads the sheet, so adding art to the tileset
 * adds it here with no code change — which is the whole point, since the art is
 * going to keep changing.
 *
 * DOM TILES, NOT ONE CANVAS, unlike `WorldCanvas`. The reasoning there was
 * 4,800 cells re-styled per brush stroke; here it is a few dozen static
 * buttons that never redraw, and buttons give keyboard focus and titles for
 * free. Each one crops the sheet with a scaled background — no per-tile image
 * slicing, and the browser does the work.
 *
 * NAMES ARE OPTIONAL. Without a manifest a tile shows as its gid, which is
 * still enough to work with and is what a downloaded tileset will look like
 * until somebody names its tiles.
 */

/** Roles the generator assigns, in the order they are offered. */
const ROLES = [
  { id: 'all', name: 'All' },
  { id: 'floor', name: 'Floor' },
  { id: 'wall', name: 'Wall' },
  { id: 'building', name: 'Building' },
  { id: 'prop', name: 'Prop' },
];

/** How big each swatch is drawn, in CSS pixels. Tiles are 16px source. */
const SWATCH = 28;

function TilePicker({
  sheet, tileset, brushGid, onBrush,
}) {
  const [role, setRole] = useState('all');

  const columns = tileset?.columns || 16;
  const firstgid = tileset?.firstgid ?? 1;
  const tileSize = sheet.tileSize || 16;

  /**
   * Which gids to offer.
   *
   * From the manifest when there is one — it lists exactly the tiles somebody
   * has drawn, so the picker is not 256 mostly-empty squares. Without one,
   * fall back to every slot the image can hold, because an unnamed tileset is
   * still fully paintable.
   */
  const gids = useMemo(() => {
    if (sheet.names.size > 0) return [...sheet.names.keys()].sort((a, b) => a - b);
    if (!sheet.image) return [];
    const rows = Math.floor(sheet.image.naturalHeight / tileSize);
    return Array.from({ length: rows * columns }, (_, i) => i + firstgid);
  }, [sheet.names, sheet.image, columns, firstgid, tileSize]);

  const shown = useMemo(() => (
    role === 'all'
      ? gids
      : gids.filter((gid) => sheet.names.get(gid)?.role === role)
  ), [gids, role, sheet.names]);

  if (!sheet.ready) {
    return (
      <p className="text-xs text-gray-500">
        {sheet.failed
          ? 'Tileset image could not be loaded — painting still works, tiles show as numbers.'
          : 'Loading tiles…'}
      </p>
    );
  }

  const sheetWidth = sheet.image.naturalWidth;
  const sheetHeight = sheet.image.naturalHeight;
  const scale = SWATCH / tileSize;

  return (
    <div>
      {/* Only worth offering when the manifest actually classifies things. */}
      {sheet.names.size > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {ROLES.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setRole(r.id)}
              className={`rounded px-1.5 py-0.5 text-[11px] ${
                role === r.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
              }`}
            >
              {r.name}
            </button>
          ))}
        </div>
      )}

      <div className="flex max-h-64 flex-wrap gap-1 overflow-y-auto rounded bg-gray-100 p-1 dark:bg-gray-900">
        {shown.map((gid) => {
          const src = tileSourceRect(gid, { columns, firstgid, tileSize });
          if (!src) return null;
          const info = sheet.names.get(gid);
          const selected = gid === brushGid;

          return (
            <button
              key={gid}
              type="button"
              title={info ? `${info.name} — gid ${gid}` : `gid ${gid}`}
              aria-label={info?.name || `Tile ${gid}`}
              aria-pressed={selected}
              onClick={() => onBrush(gid)}
              style={{
                width: SWATCH,
                height: SWATCH,
                // Crop one tile out of the sheet by scaling the whole image and
                // offsetting it — no canvas, no slicing, and it stays crisp
                // because of the pixelated rendering below.
                backgroundImage: `url(${sheet.image.src})`,
                backgroundSize: `${sheetWidth * scale}px ${sheetHeight * scale}px`,
                backgroundPosition: `-${src.sx * scale}px -${src.sy * scale}px`,
                imageRendering: 'pixelated',
              }}
              className={`shrink-0 rounded-sm ${
                selected
                  ? 'outline outline-2 outline-offset-1 outline-blue-500'
                  : 'outline outline-1 outline-black/10 hover:outline-black/30'
              }`}
            />
          );
        })}

        {shown.length === 0 && (
          <p className="px-1 py-2 text-xs text-gray-500">No tiles with that role yet.</p>
        )}
      </div>

      <p className="mt-1 text-xs text-gray-500">
        {sheet.names.get(brushGid)?.name || `Tile ${brushGid}`}
        {' · '}
        gid
        {' '}
        {brushGid}
      </p>
    </div>
  );
}

TilePicker.propTypes = {
  sheet: PropTypes.shape({
    image: PropTypes.instanceOf(Image),
    ready: PropTypes.bool,
    failed: PropTypes.bool,
    names: PropTypes.instanceOf(Map),
    tileSize: PropTypes.number,
  }).isRequired,
  tileset: PropTypes.shape({
    columns: PropTypes.number,
    firstgid: PropTypes.number,
  }),
  brushGid: PropTypes.number.isRequired,
  onBrush: PropTypes.func.isRequired,
};

TilePicker.defaultProps = { tileset: null };

export default TilePicker;
